/**
 * API functions for vendor dashboard
 */

import { authenticatedFetch } from './authApi';

export interface GeneratedChart {
  uuid: string;
  status: string;
  created_at: string;
  completed_at: string;
}

export interface VendorApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface AllocatedOrder {
  // Identificação
  ordem: number;
  item: number;
  cli_razao_social: string;
  no_vendedor: string;
  
  // Dimensões Técnicas
  prd_espessura?: number;
  prd_largura?: number;
  prd_comprimento?: number;
  
  // Status e Datas
  dt_emissao?: string;
  dt_preparacao_material?: string;
  dt_producao?: string;
  dt_criacao_remessa?: string | null;
  prd_confirmed?: boolean;
  status: string;
  qt_vol_m3_liquido_gerencial?: number;
  
  // Colunas Derivadas Calculadas (principais para exibição)
  dias_para_producao?: number;
  dias_atraso?: number;
  status_producao?: string;
  prazo_status?: string;
  volume_categoria?: string;
  dimensoes_resumo?: string;
  
  // Campos legados (mantidos para compatibilidade)
  allocated_order_id?: number;
  request_uuid?: string;
}

export interface AllocatedOrdersResponse {
  status: string;
  message: string;
  data: AllocatedOrder[];
}

// API base URL for the production backend (includes /api prefix)
const VENDOR_API_BASE = 'https://dexcobackend-v1-production.up.railway.app/api';

/**
 * Fetch list of available charts for current month
 */
export const getAvailableCharts = async (): Promise<VendorApiResponse<GeneratedChart[]>> => {
  try {
    console.log('📤 Fetching available charts...');
    
    const response = await authenticatedFetch(`${VENDOR_API_BASE}/get_optimization_id/`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const apiResponse = await response.json();
    
    if (apiResponse.status === 'success' && apiResponse.data) {
      console.log('✅ Charts fetched successfully:', apiResponse.data.length);
      
      return {
        success: true,
        data: apiResponse.data
      };
    } else {
      throw new Error(apiResponse.message || 'Erro desconhecido da API');
    }
  } catch (error) {
    console.error('❌ Error fetching charts:', error);
    return {
      success: false,
      error: 'Erro ao carregar lista de gráficos'
    };
  }
};

/**
 * Fetch CSV data for a specific chart by UUID
 */
export const getChartByUuid = async (uuid: string): Promise<VendorApiResponse<string>> => {
  try {
    console.log('📤 Fetching chart by UUID:', uuid);
    
    const response = await authenticatedFetch(`${VENDOR_API_BASE}/get_optimizer_results/?request_uuid=${uuid}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/csv'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check if response is CSV
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('text/csv') && !contentType.includes('text/plain')) {
      console.warn('⚠️ Expected CSV but got:', contentType);
    }

    const csvData = await response.text();
    
    console.log('✅ Chart CSV fetched successfully for UUID:', uuid);
    
    return {
      success: true,
      data: csvData
    };
  } catch (error) {
    console.error('❌ Error fetching chart by UUID:', error);
    return {
      success: false,
      error: `Erro ao carregar cronograma ${uuid.slice(0, 8).toUpperCase()}`
    };
  }
};

/**
 * Legacy function - kept for compatibility
 * @deprecated Use getChartByUuid instead
 */
export const getChartByHash = async (hash: string): Promise<VendorApiResponse<string>> => {
  // Assume hash is actually a UUID for backwards compatibility
  return getChartByUuid(hash);
};

export interface OrderFilters {
  vendedor?: string;
  cliente?: string;
  status?: string;
}

/**
 * Fetch allocated order status for a specific optimization batch
 * Now supports backend filtering via query parameters
 */
export const getAllocatedOrderStatus = async (
  uuid: string, 
  vendorName?: string,
  filters?: OrderFilters
): Promise<VendorApiResponse<AllocatedOrder[]>> => {
  try {
    console.log('📤 Fetching allocated order status...', { uuid, vendorName, filters });
    
    // Build URL with parameters
    const params = new URLSearchParams();
    params.append('uuid', uuid);
    
    // Add vendor filter (for vendor users)
    if (vendorName) {
      params.append('no_vendedor', vendorName);
    }
    
    // Add additional filters from UI
    if (filters) {
      if (filters.vendedor) {
        params.append('no_vendedor', filters.vendedor);
      }
      if (filters.cliente) {
        params.append('cli_razao_social', filters.cliente);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
    }
    
    const url = `${VENDOR_API_BASE}/allocated_order_status/?${params.toString()}`;
    console.log('🔗 API URL:', url);
    
    const response = await authenticatedFetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const apiResponse: AllocatedOrdersResponse = await response.json();
    
    if (apiResponse.status === 'success' && apiResponse.data) {
      console.log('✅ Allocated orders fetched successfully:', apiResponse.data.length);
      
      return {
        success: true,
        data: apiResponse.data
      };
    } else {
      throw new Error(apiResponse.message || 'Erro desconhecido da API');
    }
  } catch (error) {
    console.error('❌ Error fetching allocated orders:', error);
    return {
      success: false,
      error: `Erro ao carregar status das ordens para ${uuid.slice(0, 8).toUpperCase()}`
    };
  }
};

/**
 * Format chart date for display
 */
export const formatChartDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format optimization status for display
 */
export const formatStatus = (status: string): { label: string; color: string } => {
  switch (status.toLowerCase()) {
    case 'completed':
      return { label: 'Concluído', color: 'text-green-600' };
    case 'running':
    case 'pending':
      return { label: 'Em Andamento', color: 'text-yellow-600' };
    case 'error':
    case 'failed':
      return { label: 'Erro', color: 'text-red-600' };
    default:
      return { label: status, color: 'text-gray-600' };
  }
};

/**
 * Get relative time description (e.g., "há 2 dias")
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (diffDays > 0) {
    return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  } else if (diffMinutes > 0) {
    return `há ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
  } else {
    return 'agora mesmo';
  }
}; 